import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import {
  StyledBox,
  StyledBoxDis,
  StyledBoxFlex,
  StyledBoxInner,
  StyledBoxPad,
  StyledBoxWidthInput,
  StyledDivider,
  StyledOrBox,
  StyledTypographyDo,
  StyledTypographyOr,
  StyledTypographyWel,
  StyleSpan,
  StyleTypographyText,
} from "../../../pages/auth/sign-up/style";

import { useNavigate } from "react-router-dom";
import CustomInput from "../../common/input/CustomInput";
import CustomButton from "../../common/button/CustomButton";
import theme from "../../../theme";
import SocialButton from "../../common/social-button/SocialButton";
import goggle from "../../../assets/images/goggle.png";
import { getPhoneValidation } from "../../../utils/countryRules";

const getValidationSchema = (currentCountryCode) => {
  return Yup.object({
    firstName: Yup.string()
      .trim()
      .required("First name is required")
      .matches(
        /^[A-Za-z]+(?: [A-Za-z]+)*$/,
        "First name can only contain letters"
      ),
    lastName: Yup.string()
      .trim()
      .required("Last name is required")
      .matches(
        /^[A-Za-z]+(?: [A-Za-z]+)*$/,
        "Last name can only contain letters"
      ),

    email: Yup.string()
      .trim()
      .required("Email is required")
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
        "Enter a valid email address"
      ),

    phone: Yup.string()
      .trim()
      .required("Phone number is required")
      .test(getPhoneValidation(currentCountryCode)),

  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/\d/, "Password must contain at least one number")
    .matches(
      /[@$!%*?&]/,
      "Password must contain at least one special character"
    ),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });
};

const SignUpForm = ({ onSubmit, isSubmitting = false }) => {
  const navigate = useNavigate();
  const [currentCountryCode, setCurrentCountryCode] = React.useState("+91");

  const handleLogin = () => {
    navigate("/login");
  };

  const handleGoogleLogin = () => {
    const googleAuthUrl = `${process.env.REACT_APP_API_URL}/auth/google`;
    window.open(googleAuthUrl, "_self");
  };

  return (
    <StyledBox>
      <StyledBoxInner>
        <StyledTypographyWel>WELCOME BACK</StyledTypographyWel>
        <StyleTypographyText>Sign Up to your Account</StyleTypographyText>
        <Formik
          initialValues={{
            firstName: "",
            password: "",
            lastName: "",
            phone: "",
            email: "",
            confirmPassword: "",
          }}
          validationSchema={getValidationSchema(currentCountryCode)}
          onSubmit={(values) => {
            onSubmit({
              first_name: values.firstName,
              last_name: values.lastName,
              email: values.email.trim(),
              phone_number: values.phone
                ? `${currentCountryCode} ${values.phone}`
                : "",
              password: values.password,
            });
          }}
        >
          {({ errors, handleChange, handleBlur, values, touched }) => (
            <Form>
              <StyledBoxDis>
                <StyledBoxFlex>
                  <StyledBoxWidthInput>
                    <CustomInput
                      label="First Name"
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                    />
                  </StyledBoxWidthInput>
                  <StyledBoxWidthInput>
                    <CustomInput
                      label="Last Name"
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                    />
                  </StyledBoxWidthInput>
                </StyledBoxFlex>

                <CustomInput
                  label="Phone Number"
                  name="phone"
                  value={values.phone}
                  type="number"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone && errors.phone}
                  isPhoneNumber={true}
                  currentCountryCode={currentCountryCode}
                  setCurrentCountryCode={setCurrentCountryCode}
                />

                <CustomInput
                  label="Email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
                <CustomInput
                  label="Password"
                  name="password"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && errors.password}
                  helperText={touched.password && errors.password}
                />
                <CustomInput
                  label="Confirm Password "
                  name="confirmPassword"
                  type="password"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.confirmPassword && errors.confirmPassword}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                />
                <StyledBoxPad>
                  <CustomButton
                    fontSize={theme.typography.regular.fontSize}
                    type="submit"
                    btnLabel="GET STARTED"
                    btntype={"primary"}
                    height="56px"
                    fullWidth
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                  />
                </StyledBoxPad>

                <StyledOrBox>
                  <StyledDivider />
                  <StyledTypographyOr>Or</StyledTypographyOr>
                  <StyledDivider />
                </StyledOrBox>
                <SocialButton
                  height="48px"
                  label="Sign up with Google"
                  icon={goggle}
                  onClick={handleGoogleLogin}
                />

                <StyledTypographyDo>
                  Already have an account?
                  <StyleSpan onClick={() => handleLogin()}>
                    Login a Account
                  </StyleSpan>
                </StyledTypographyDo>
              </StyledBoxDis>
            </Form>
          )}
        </Formik>
      </StyledBoxInner>
    </StyledBox>
  );
};

export default SignUpForm;
